import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { get_basket_thunk } from "../../../reducers/slices/basket/thunks/get_basket_thunk";
import { calculate_basket_thunk } from "../../../reducers/slices/basket/thunks/calculate_basket_thunk";
import { setMainModal } from "../../../reducers/slices/main";
import { remove_products } from "../../../api/calls/basket/remove_products";
import { cancel_basket } from "../../../api/calls/basket/cancel_basket";
import { issueVoucher } from "../../../api/calls/product/issue_voucher";
import { update_basket_voucher } from "../../../api/calls/basket/update_basket_voucher";
export function useBasketState() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [loadingApplyVoucher, setLoadingApplyVoucher] = useState(false);
    const { basket } = useSelector((state) => state.basketState);
    const { user } = useSelector((state) => state.userState);
    const { formState: { errors }, handleSubmit, register, } = useForm({ defaultValues: { products: [] } });
    const { control: voucherControl, formState: { isValid: isVoucherValid }, handleSubmit: submitForm, register: registerVoucher, watch: watchVoucher, reset, } = useForm({ defaultValues: { voucher: "" } });
    const voucher = watchVoucher("voucher");
    register("products", {
        min: { value: 0, message: "Quantity cannot be less than 0" },
        required: { value: true, message: "Please specify a quantity to add to cart." },
    });
    registerVoucher("voucher", { required: false });
    const basketId = useMemo(() => basket?._id ?? "", [basket]);
    const items = useMemo(() => basket?.product_list ?? [], [basket]);
    const itemCount = useMemo(() => basket?.product_list?.length ?? 0, [basket]);
    const hasItems = useMemo(() => !!basket?.product_list && basket?.product_list?.length > 0, [basket]);
    const hasVoucherCode = useMemo(() => !!voucher, [voucher]);
    const enableApplyVoucher = useMemo(() => !!hasVoucherCode && !!isVoucherValid, [hasVoucherCode, isVoucherValid]);
    const subtotal = useMemo(() => basket?.sub_total ?? 0, [basket]);
    const shipping = useMemo(() => basket?.delivery_fee ?? 0, [basket]);
    const total = useMemo(() => basket?.total ?? 0, [basket]);
    const outstandingAmount = useMemo(() => basket?.outstanding_amount ?? basket?.total ?? 0, [basket]);
    const voucherDiscountTotal = parseFloat(basket?.discount_total ?? "0") || 0;
    const handleClearBasketClicked = () => {
        cancel_basket({
            _id: basket?._id,
        }).then((response) => {
            if (response.success) {
                dispatch(get_basket_thunk());
                navigate("/shop");
            }
            else {
                dispatch(setMainModal({
                    title: "Something went wrong!",
                    messages: [response.message],
                }));
            }
        });
    };
    const handleRemoveItemFromBasket = (item_id) => {
        remove_products({
            _id: basket?._id,
            product_id_list: [item_id],
        }).then((response) => {
            if (response.success) {
                dispatch(get_basket_thunk());
            }
            else {
                dispatch(setMainModal({ title: "Something went wrong!", messages: [response.message] }));
            }
        });
    };
    const handleApplyVoucher = async (data) => {
        const { voucher } = data;
        const trimmedCode = voucher.trim();
        if (!trimmedCode) {
            dispatch(setMainModal({
                title: "Voucher Error",
                messages: ["Please enter a voucher code."],
            }));
            return;
        }
        if (!user?.user?._id) {
            dispatch(setMainModal({ title: "User not found", messages: ["You need to be logged in to redeem a voucher."] }));
            return;
        }
        setLoadingApplyVoucher(true);
        try {
            const voucherResponse = await issueVoucher({
                user_id: user.user._id,
                voucher_code: trimmedCode,
            });
            if (!voucherResponse.success) {
                dispatch(setMainModal({
                    title: "Voucher Error",
                    messages: ["Unable to issue voucher. Please try again."],
                }));
                return;
            }
            const basketResponse = await update_basket_voucher({
                basket_id: basketId,
                voucher_code: trimmedCode,
                applied: true,
            });
            if (basketResponse.success && basketResponse.content?._id) {
                await dispatch(calculate_basket_thunk(basketResponse));
                reset();
            }
            else {
                dispatch(setMainModal({
                    title: "Unable to apply voucher",
                    messages: [basketResponse.message ?? "We could not apply this voucher. Please try again."],
                }));
            }
        }
        catch (error) {
            dispatch(setMainModal({
                title: "Unable to apply voucher",
                messages: ["We encountered an unexpected error while applying the voucher."],
            }));
        }
        finally {
            setLoadingApplyVoucher(false);
        }
    };
    const proceedToCheckout = () => {
        navigate("/checkout");
    };
    return useMemo(() => ({
        proceedToCheckout,
        handleApplyVoucher,
        handleClearBasketClicked,
        handleRemoveItemFromBasket,
        handleSubmit,
        hasItems,
        submitForm,
        enableApplyVoucher,
        basketId,
        items,
        itemCount,
        shipping,
        subtotal,
        total,
        outstandingAmount,
        voucherControl,
        loadingApplyVoucher,
        voucherDiscountTotal,
    }), [
        proceedToCheckout,
        handleApplyVoucher,
        handleClearBasketClicked,
        handleRemoveItemFromBasket,
        handleSubmit,
        hasItems,
        submitForm,
        enableApplyVoucher,
        basketId,
        items,
        itemCount,
        shipping,
        subtotal,
        total,
        outstandingAmount,
        voucherControl,
        loadingApplyVoucher,
        voucherDiscountTotal,
    ]);
}
